import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from "@ngx-translate/core";
import { FormBuilder, FormGroup, Validators, ValidatorFn, AbstractControl } from '@angular/forms';
import { firstValueFrom } from 'rxjs';
import { ItemResponse, ItemsService, ProductsService, CompanyResponse, FilterRequestForCombo, ItemDisposition } from '@src/app/shared/backend/clients';
import { FilterOperator } from '@app/shared/enums/FilterOperator.enum';
import { SessionService } from '@src/app/shared/services/session.service';
import { BaseViewComponent } from '@src/app/shared/components/base-list/base-view.component';

@Component({
    selector: 'app-edit-item',
    templateUrl: './edit-item.component.html',
    styleUrl: './edit-item.component.scss',
    host: {
        class: 'app-edit-item'
    },
    standalone: false
})
export class EditItemComponent extends BaseViewComponent implements OnInit {

  showSecurityCodeField: boolean = false;
  showSecurityCodeVersionField: boolean = false;

  companySettings: CompanyResponse | undefined;
  companyId: number | undefined;
  isPrepaired: boolean = false;

  itemId: number = 0;
  item: ItemResponse = {};
  itemsList: any;
  currentDate: Date = new Date();
  filterOperator = FilterOperator;

  product: any = {}; // ProductDetailsResponse
  products: any[] = [];
  productTotalRecords: number = 0;
  productRowsPerPageOption: number = 10;

  companyHasCpo: boolean = false;

  public itemForm: FormGroup = this.fb.group({
    product: [[], [Validators.required]],
    productId: [''],
    serialNumber: ['', [Validators.required, Validators.maxLength(100)]],
    securityCode: [null],
    securityCodeVersion: [null],
    productionDate: [null],
    warrantyCode: [null, [Validators.maxLength(50)]],
    warrantyDurationMonths: [null],
    isCpo: ['false'],
    isGenuine: ['true']
  },
    { validators: this.conditionalValidators() });

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private translate: TranslateService,
    private session: SessionService,
    private itemsService: ItemsService,
    private productsService: ProductsService,
    private fb: FormBuilder) { super(); }

  ngOnInit() {
    this.init();
  }

  private async init(): Promise<void> {
    this.route.params.subscribe(async params => {
      this.itemId = params['itemId'];

      if (this.itemId) {
        this.item = await firstValueFrom(this.itemsService.apiItemsIdGet(this.itemId));
        if(this.item.disposition === ItemDisposition.Sold || this.item.disposition === ItemDisposition.Stolen){
          this.router.navigate([`/error`]);
        }

        if (this.item.productId) {
          this.product = await firstValueFrom(
            this.productsService.apiProductsDetailsIdGet(this.item.productId)
          );
        }

        await this.getProducts({ pageIndex: 1, pageSize: this.productRowsPerPageOption, search: '' });
        this.initForm();
      }
    });

    this.session.currentCompanyData$.subscribe(company => {
      this.showSecurityCodeField = company?.showSecurityCode ?? false;
      this.showSecurityCodeVersionField = company?.showSecurityCodeVersion ?? false;
      this.companyHasCpo = !!company?.hasCpo;
    });

    await this.getProducts({ pageIndex: 1, pageSize: this.productRowsPerPageOption, search: '' });
    await this.getCompanySettings();
  }

  async getCompanySettings() {
    const company = await this.session.getCompanyData(); // cached

    this.companySettings = company;
    this.isPrepaired = company?.isPrepaired ?? false;

    // force revalidation
    this.itemForm.updateValueAndValidity();
  }

  qrCodeScanned(data: any) {
    this.itemForm.controls['serialNumber'].setValue(data, { emitEvent: false });
  }

  isPrepairedRequired(): boolean {
    return this.isPrepaired;
  }

  conditionalValidators(): ValidatorFn {
    return (formGroup: AbstractControl): { [key: string]: any } | null => {
      const warrantyCode = formGroup.get('warrantyCode')?.value;

      if (this.isPrepaired && !warrantyCode) {
        return { requiredWarrantyCode: true };
      }

      return null;
    };
  }

  onLazyLoadProducts(event: any) {
    this.getProducts(event);
  }

  async getProducts(event: any) {
    const search = event?.search?.trim() || null;
    const activePage = Math.max(1, (event?.pageIndex ?? 1));
    const payload : FilterRequestForCombo = {
      pageNumber: activePage,
      pageSize: event?.pageSize ?? this.productRowsPerPageOption
    }
    if (search) {
      payload.textFilter = search;
    }

    const productsPage = await firstValueFrom(this.productsService.apiProductsFilterComboPost(payload));
    this.products = productsPage.results ?? [];
    this.productTotalRecords = productsPage.totalCount ?? 0;

    const selectedProduct = this.product ?? null;
    if (selectedProduct?.id) {
      const exists = this.products.some((p: any) => p.id === selectedProduct.id);
      if (!exists) {
        const current = await firstValueFrom(this.productsService.apiProductsComboValueIdGet(selectedProduct.id))
        this.products = [current, ...this.products];
      }
    }
  }

  initForm() {
    if (this.product?.id) {
      const exists = this.products.some((p: any) => p.id === this.product.id);
      if (!exists) {
        this.products = [this.product, ...this.products];
      }
    }

    this.itemForm.controls['product'].setValue(this.item.productId ? [this.item.productId] : []);
    this.itemForm.controls['productId'].setValue(this.item.productId ?? null);

    this.itemForm.controls['serialNumber'].setValue(this.item.serialNumber);
    this.itemForm.controls['securityCode'].setValue(this.item.securityCode);
    this.itemForm.controls['securityCodeVersion'].setValue(this.item.securityCodeVersion);

    if (this.item.productionDate) {
      this.itemForm.controls['productionDate'].setValue(new Date(this.item.productionDate));
    }

    this.itemForm.controls['warrantyDurationMonths'].setValue(this.item.warrantyDurationMonths);
    this.itemForm.controls['isCpo'].setValue(this.item.isCpo ? 'true' : 'false');
    this.itemForm.controls['isGenuine'].setValue(this.item.isGenuine ? 'true' : 'false');

    if(this.isPrepaired) {
      this.itemForm.controls['warrantyCode'].setValue(this.item.warranties?.find(w => w.isPrepaired)?.code);
    }

  }

  getTotalPages(totalRecords: number, pageSize: number): number {
    return Math.max(1, Math.ceil((totalRecords ?? 0) / (pageSize || 1)));
  }

  async submitForm() {
    if (this.itemForm.valid) {
      const payload = {
        productId: this.itemForm.controls['product'].value?.[0],
        serialNumber: this.itemForm.controls['serialNumber'].value,
        securityCode: this.itemForm.controls['securityCode'].value,
        securityCodeVersion: this.itemForm.controls['securityCodeVersion'].value,
        productionDate: this.itemForm.controls['productionDate'].value,
        warrantyDurationMonths: this.itemForm.controls['warrantyDurationMonths'].value,
        isCpo: this.itemForm.controls['isCpo'].value === 'true' ? true : false,
        isGenuine: this.itemForm.controls['isGenuine'].value === 'true' ? true : false,
        warrantyCode: this.itemForm.controls['warrantyCode']?.value
      };

      if(!this.isPrepaired) {
        await firstValueFrom(this.itemsService.apiItemsUpdateIdPut(this.itemId, payload));
      }
      else {
        await firstValueFrom(this.itemsService.apiItemsUpdatePrepairedIdPut(this.itemId, payload));
      }

      this.router.navigate(['/items', this.itemId]);
    }
  }
}
